import { useLayoutEffect, useRef, useState } from "react";

import { classNames } from "@alpacahq/ui/old";

export interface ToggleOption<T> {
  label: string;
  value: T;
}

type StringifiableValue = string | number | boolean;

export interface ToggleOptionsProps<T extends StringifiableValue = string> {
  ariaLabel?: string;
  size?: "xs" | "sm" | "lg";
  name?: string;
  options: ToggleOption<T>[];
  value: T | null | undefined;
  onChange: (value: T) => void;
  className?: string;
}

const tokens = {
  sizes: {
    xs: {
      container: "h-6",
      label: "text-[10px] px-2 py-0.5",
    },
    sm: {
      container: "h-8",
      label: "text-xs px-4 py-1.5",
    },
    lg: {
      container: "h-9",
      label: "text-sm px-4 py-2",
    },
  },
  label: {
    base: "block cursor-pointer whitespace-nowrap rounded-full transition-colors duration-300 font-medium",
    selected: "text-white",
    unselected: "text-gray-400 hover:text-gray-700",
  },
};

export function ToggleOptions<T extends StringifiableValue = string>({
  ariaLabel,
  size = "sm",
  name,
  options,
  value,
  onChange,
  className,
}: ToggleOptionsProps<T>) {
  const optionRefs = useRef<(HTMLLabelElement | null)[]>([]);
  const [orbStyle, setOrbStyle] = useState<{
    width: number;
    transform: string;
  } | null>(null);

  useLayoutEffect(() => {
    const updateOrbPosition = () => {
      const refs = optionRefs.current.filter(Boolean);
      if (refs.length > 0 && refs.length === options.length) {
        if (value === null || value === undefined) {
          setOrbStyle(null);
          return;
        }
        const selectedIndex = options.findIndex(
          (option) => option.value === value,
        );
        const selectedRef = refs[selectedIndex];
        if (selectedIndex === -1 || !selectedRef) {
          setOrbStyle(null);
          return;
        }
        const selectedWidth = selectedRef.getBoundingClientRect().width;
        // Calculate the transform offset by summing widths of previous options
        const transformOffset = refs
          .slice(0, selectedIndex)
          .reduce(
            (acc, ref) => acc + (ref ? ref.getBoundingClientRect().width : 0),
            0,
          );
        setOrbStyle({
          width: selectedWidth,
          transform: `translateX(${transformOffset}px)`,
        });
      }
    };
    // Update position after render
    setTimeout(updateOrbPosition, 0);
    // Update on window resize
    window.addEventListener("resize", updateOrbPosition);
    return () => {
      window.removeEventListener("resize", updateOrbPosition);
    };
  }, [value, options]);

  return (
    <fieldset className="relative">
      {ariaLabel && <legend className="sr-only">{ariaLabel}</legend>}
      <div
        className={classNames(
          "relative flex w-fit items-center justify-center rounded-full bg-gray-100 p-1",
          tokens.sizes[size].container,
          className,
        )}
      >
        {orbStyle && (
          <div
            className="absolute top-1 left-1 z-0 rounded-3xl bg-indigo-500 transition-all duration-700 ease-in-out"
            style={{
              width: `${orbStyle.width}px`,
              height: "calc(100% - 0.5rem)",
              transform: orbStyle.transform,
            }}
          />
        )}
        {options.map((option, index) => {
          const isSelected = value === option.value;
          return (
            <label
              key={`toggle-option-${String(option.value)}`}
              ref={(el) => {
                optionRefs.current[index] = el;
              }}
              className={classNames(
                "relative z-10",
                tokens.label.base,
                tokens.sizes[size].label,
                isSelected ? tokens.label.selected : tokens.label.unselected,
              )}
            >
              <input
                type="radio"
                name={name}
                value={String(option.value)}
                checked={isSelected}
                onChange={() => onChange(option.value)}
                className="hidden"
              />
              {option.label}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
